import { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from '@mui/material';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import { LatLngTuple } from 'leaflet';
import { ApiRequest } from 'GlobalFunctions';
import { ProjectConfig } from 'Global';

const emergencyIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const okIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

interface SafetyAlertDetailsProps {
  alertId: number;
  open: boolean;
  onClose: () => void;
}

export default function SafetyAlertDetails({ alertId, open, onClose }: SafetyAlertDetailsProps) {
  const [alert, setAlert] = useState<any>(null);
  const [responses, setResponses] = useState<any[]>([]);

  useEffect(() => {
    if (open && alertId) loadDetails();
  }, [open, alertId]);

  const loadDetails = async () => {
    await ApiRequest({
      url: `/safety-alerts/details/${alertId}`,
      method: 'get',
      headers: { ltkn: localStorage.getItem('ltkn') },
      setResponse: (response: any) => {
        setAlert(response.alert);
        setResponses(response.responses || []);
      },
    });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
  };

  const mapPositions: LatLngTuple[] = responses
    .filter((r: any) => r.latitude && r.longitude)
    .map((r: any) => [parseFloat(r.latitude), parseFloat(r.longitude)]);

  const typeLabel = (type: string) => {
    switch (type) {
      case 'periodic': return 'Periódica';
      case 'manual': return 'Manual';
      case 'emergency': return 'Emergencia';
      default: return type;
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'sent': return <Chip label="Enviado" size="small" color="default" />;
      case 'received': return <Chip label="Recibido" size="small" color="info" />;
      case 'read': return <Chip label="Leído" size="small" color="warning" />;
      case 'closed': return <Chip label="Respondido" size="small" color="success" />;
      default: return status;
    }
  };

  const responseLabel = (response: string) => {
    if (response === 'yes') return <Chip label="Bien" size="small" color="success" />;
    if (response === 'no') return <Chip label="Emergencia" size="small" color="error" />;
    return '-';
  };

  const emergencyTypeLabel = (type: string) => {
    switch (type) {
      case 'ATRACO': return 'Atraco';
      case 'ACCIDENTE': return 'Accidente';
      case 'MAL_DIA': return 'Mal día';
      default: return type || '-';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Detalle de Alerta #{alert?.company_alert_id}
        {alert && (
          <Typography variant="body2" color="textSecondary">
            Tipo: {typeLabel(alert.alert_type)} | Fecha: {formatDate(alert.created_at)}
            {alert.triggered_by_name && ` | Enviada por: ${alert.triggered_by_name} ${alert.triggered_by_last_name || ''}`}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {/* Map */}
        {mapPositions.length > 0 && (
          <div style={{ height: 350, marginBottom: 20 }}>
            <MapContainer
              center={mapPositions[0]}
              zoom={12}
              style={{ height: '100%', width: '100%', borderRadius: 8 }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {responses
                .filter((r: any) => r.latitude && r.longitude)
                .map((r: any, i: number) => (
                  <Marker
                    key={r.response_id}
                    position={[parseFloat(r.latitude), parseFloat(r.longitude)]}
                    icon={r.response === 'no' ? emergencyIcon : okIcon}
                  >
                    <Popup>
                      <strong>{r.name} {r.last_name}</strong><br />
                      {r.response === 'no' ? `🚨 ${emergencyTypeLabel(r.emergency_type)}` : '✅ Todo bien'}<br />
                      {r.note && <><em>{r.note}</em><br /></>}
                      {formatDate(r.responded_at)}
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
          </div>
        )}

        {/* Responses table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Respuesta</TableCell>
                <TableCell>Tipo Emergencia</TableCell>
                <TableCell>Nota</TableCell>
                <TableCell>Foto</TableCell>
                <TableCell>Fecha Respuesta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map((r: any) => (
                <TableRow
                  key={r.response_id}
                  sx={r.response === 'no' ? { backgroundColor: '#ffebee' } : {}}
                >
                  <TableCell>{r.name} {r.last_name}</TableCell>
                  <TableCell>{statusLabel(r.status)}</TableCell>
                  <TableCell>{responseLabel(r.response)}</TableCell>
                  <TableCell>{emergencyTypeLabel(r.emergency_type)}</TableCell>
                  <TableCell>{r.note || '-'}</TableCell>
                  <TableCell>
                    {r.photo_url ? (
                      <a
                        href={r.photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver foto
                      </a>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{formatDate(r.responded_at)}</TableCell>
                </TableRow>
              ))}
              {responses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Sin respuestas aún</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
